import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ClipboardCheck, CheckCircle2, Clock, AlertTriangle, ArrowRight, PartyPopper } from 'lucide-react';
import Header from '../components/Header';
import DIASReadinessWidget from '../components/DIASReadinessWidget';
import { useAuth } from '../context/AuthContext';
import { SECTIONS } from '../lib/sectionTheme';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const D = SECTIONS.dashboard;
const S = SECTIONS.sops;

export default function HomePage() {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [sopTitles, setSopTitles] = useState({});

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/sops/assignments`, { withCredentials: true })
      .then(r => setAssignments(r.data.filter(a => a.user_id === user.id)))
      .catch(() => {});
    axios.get(`${API}/sops`, { withCredentials: true })
      .then(r => setSopTitles(Object.fromEntries(r.data.map(s => [s.sop_id, s.title]))))
      .catch(() => {});
  }, [user]);

  const today = new Date().toISOString().split('T')[0];
  const pending = assignments.filter(a => a.status !== 'completed');
  const completed = assignments.filter(a => a.status === 'completed');
  const overdue = pending.filter(a => a.due_date && a.due_date < today);
  const total = assignments.length;
  const completionPct = total > 0 ? Math.round((completed.length / total) * 100) : 0;

  const hasAnyTasks = total > 0;
  const fullyOnboarded = hasAnyTasks && pending.length === 0;

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="home-dashboard">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Greeting */}
        <div className="mb-8" data-testid="home-greeting">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4" style={{ color: D.color }} />
            <span className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: D.color }}>Dashboard</span>
          </div>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Hello, <span style={{ color: D.color }}>{user.name?.split(' ')[0]}</span>
          </h1>
        </div>

        {/* DIAS readiness scorecard — admin / PM only. Renders nothing for
            other roles (the backend gate returns 403 and the component
            silently hides). */}
        {(user.role === 'admin' || user.role === 'superuser') && <DIASReadinessWidget />}

        {/* Onboarding intro + progress ring */}
        {hasAnyTasks ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10">
            <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-6" data-testid="onboarding-intro"
              style={{ borderLeftWidth: 4, borderLeftColor: S.color }}>
              <div className="flex items-center gap-2 mb-2">
                <ClipboardCheck className="w-4 h-4" style={{ color: S.color }} />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: S.color }}>Your onboarding</span>
              </div>
              <h2 className="font-['Outfit'] text-xl font-semibold text-foreground">
                {fullyOnboarded
                  ? 'You are fully onboarded 🎉'
                  : `${pending.length} SOP${pending.length === 1 ? '' : 's'} to complete`}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                {fullyOnboarded
                  ? 'Every SOP assigned to you has been read and signed off. Your manager can see this on the governance dashboard.'
                  : "As part of joining i3 Radiology, you need to read and sign off on the Standard Operating Procedures below. They cover patient care, safety and how we work together. Click any card to start the walkthrough — it's step-by-step, so you can't miss anything important."}
              </p>
              {overdue.length > 0 && (
                <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-red-500/10 text-red-600" data-testid="overdue-banner">
                  <AlertTriangle className="w-3.5 h-3.5" /> {overdue.length} overdue — please prioritise these
                </div>
              )}
            </div>

            <div className="bg-card border border-border rounded-2xl p-6 flex flex-col" data-testid="sop-progress-card">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Completion</span>
              <div className="flex-1 flex items-center justify-center my-3">
                <ProgressRing value={completionPct} color={S.color} size={140} />
              </div>
              <div className="text-center text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">{completed.length}</span> of <span className="font-semibold text-foreground">{total}</span> SOPs signed off
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-2xl p-8 text-center mb-10" data-testid="no-tasks-card">
            <PartyPopper className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
            <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">No SOP tasks yet</h2>
            <p className="mt-2 text-sm text-muted-foreground max-w-lg mx-auto">
              You have nothing to complete right now. Explore the resource tabs above — the Kestral K4 Resource is a great place to start.
            </p>
          </div>
        )}

        {/* My SOP tasks — replaces the "My Tasks" section from /sops */}
        {hasAnyTasks && (
          <>
            <div className="flex items-center justify-between mb-4" data-testid="my-sops-header">
              <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">My SOPs</h2>
              <span className="text-xs text-muted-foreground">Click a card to start the walkthrough</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-sops-grid">
              {[...pending, ...completed].map((a) => {
                const isOverdue = a.status !== 'completed' && a.due_date && a.due_date < today;
                const isDone = a.status === 'completed';
                const title = sopTitles[a.sop_id] || 'SOP';
                const borderColor = isDone ? '#16A34A' : isOverdue ? '#EF4444' : S.color;
                return (
                  <Link key={a.assignment_id} to={`/sops/walkthrough/${a.assignment_id}`}
                    data-testid={`my-sop-${a.assignment_id}`}
                    className="group relative bg-card border border-border rounded-2xl p-5 hover:shadow-lg transition-all"
                    style={{ borderLeftWidth: 4, borderLeftColor: borderColor }}>
                    <div className="flex items-start justify-between mb-3">
                      {isDone
                        ? <CheckCircle2 className="w-5 h-5 text-green-500" />
                        : isOverdue
                          ? <AlertTriangle className="w-5 h-5 text-red-500" />
                          : <Clock className="w-5 h-5" style={{ color: S.color }} />}
                      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                    </div>
                    <h3 className="font-['Outfit'] text-base font-semibold text-foreground leading-snug">{title}</h3>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${borderColor}15`, color: borderColor }}>
                        {isDone ? 'Completed' : isOverdue ? 'Overdue' : 'Pending'}
                      </span>
                      {a.due_date && <span className="text-[11px] text-muted-foreground">Due {a.due_date}</span>}
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ProgressRing({ value, color, size = 120 }) {
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (value / 100) * circ;
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size/2} cy={size/2} r={radius} fill="none" strokeWidth={stroke} className="stroke-muted" />
      <circle cx={size/2} cy={size/2} r={radius} fill="none" strokeWidth={stroke} strokeLinecap="round"
        stroke={color} strokeDasharray={circ} strokeDashoffset={offset} style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
      <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" className="rotate-90 origin-center font-['Outfit'] fill-foreground" fontSize={size/5} fontWeight={700}>
        {value}%
      </text>
    </svg>
  );
}
